import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { useI18n } from 'vue-i18n';
import { request } from '@/api/request';
import {
  mergeMutationOptions,
  mergeQueryOptions,
  type QueryOptions,
  type MutationOptions,
} from '@/modules/query/utils';
import { isAuthenticatedByRLRef } from '@/modules/auth/state';
import { useCurrentOrg, useOrgUsers } from '@/modules/current-org/api';
import { ORG_USERS_QUERY } from '@/modules/current-org/api/queryKeys';
import type { Role } from '@@/packages/shared/types/models/roles';
import { CURRENT_ORG_ROLES_QUERY } from './queryKeys';

export function useCurrentOrgRoles(options?: QueryOptions) {
  const { currentOrg } = useCurrentOrg();
  const { allOrgUsers } = useOrgUsers();

  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [CURRENT_ORG_ROLES_QUERY],
      async queryFn() {
        const res = await request<Role[]>('GET', '/roles');
        return res.data;
      },
      enabled: isAuthenticatedByRLRef,
    }),
  );

  const { t } = useI18n();

  const roleLabel = (roleId: string | undefined, isLead = false) => {
    if (isLead) return t('roles.lead');
    if (!roleId) return t('roles.unknown');
    const role = query.data.value?.find((r) => r.id === roleId);
    if (!role) return t('roles.unknown');
    return role.orgID ? role.name : t(`roles.${role.name}`);
  };

  const roles = computed<Role[]>(
    () =>
      query.data.value
        ?.filter((role) => {
          if (role.orgID) return true;
          if (Object.values(allOrgUsers.value).find(({ roleId }) => roleId === role.id)) {
            return true;
          }
          return (
            (!role.name.includes('client') && !role.name.includes('supplier')) ||
            (currentOrg.value?.isClient && currentOrg.value?.isSupplier)
          );
        })
        .sort((a, b) => {
          if (a.orgID) {
            if (!b.orgID) return 1;
            return a.createdAt < b.createdAt ? -1 : 1;
          }
          if (b.orgID) return -1;
          return roleLabel(a.id).toLowerCase() < roleLabel(b.id).toLowerCase() ? -1 : 1;
        }) || [],
  );

  return { ...query, roles, roleLabel };
}

type CreateRoleBody = {
  name: string;
  description?: string;
  permissions: string[];
};

export function useCreateRole(options?: MutationOptions<Role, CreateRoleBody>) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(role) {
        const res = await request<Role>('POST', '/roles', role);
        return res.data;
      },
      async onSuccess(data) {
        await queryClient.setQueryData([CURRENT_ORG_ROLES_QUERY], (oldData: Role[] | undefined) => {
          if (!oldData) return undefined;
          return [...oldData, data];
        });
      },
    }),
  );
}

type UpdateRoleBody = {
  name: string;
  description?: string;
  permissions: string[];
};

export function useUpdateRole(
  options?: MutationOptions<Role, { id: string; role: UpdateRoleBody }>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ id, role }) {
        const res = await request<Role>('PUT', `/roles/${id}`, role);
        return res.data;
      },
      async onSuccess(data) {
        await queryClient.setQueryData([CURRENT_ORG_ROLES_QUERY], (oldData: Role[] | undefined) =>
          oldData?.map((oldRole) => (oldRole.id === data.id ? data : oldRole)),
        );
      },
    }),
  );
}

type DeleteRole = {
  roleID: string;
  replacementRoleID?: string;
};

export function useDeleteRole(options?: MutationOptions<null, DeleteRole>) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ roleID, replacementRoleID }) {
        const res = await request<null>('DELETE', `/roles/${roleID}`, { replacementRoleID });
        return res.data;
      },
      async onSuccess(_, { roleID, replacementRoleID }) {
        await queryClient.setQueryData([CURRENT_ORG_ROLES_QUERY], (oldData: Role[] | undefined) => {
          if (!oldData) return undefined;
          return oldData.filter((role) => role.id !== roleID);
        });
        if (replacementRoleID) {
          await queryClient.invalidateQueries({ queryKey: [ORG_USERS_QUERY] });
        }
      },
    }),
  );
}
